import { useCallback, useMemo, useState, memo, useEffect, useRef } from 'react';
import CommRecordsDrawer from './comm-records-drawer';
import CollectAction from '@/sub-luca-plugin/components/collect-action';
import { eventBusExample, navigateExample } from '@/sub-luca-plugin/utils/class-example';
import { getQaStore, useQaStore } from '@/sub-luca-plugin/store/qa';
import { getChatSseHeaders } from '../dialogue-area/index.helper';
import { createNewQuestion } from '../helper';
import NavigationHeaderWapper from '@/sub-luca-plugin/components/navigation-header-wapper';
import NewQuestionBtn from './new-question-btn';
import { get_summary } from '@/sub-luca-plugin/apis/qa';
import { INoticeData } from '@/sub-luca-plugin/types';
import './index.scss';

const NavgationBar = () => {
    const { qaConfig, updateQaConfig, addRequestController } = useQaStore();
    const {
        id: commId,
        transferLaborState,
        projectId,
        lucaId,
        projectName = '',
        subjectName = '',
        twoLevelSubjectName = '',
        questionTag,
        askChatTitle = '',
        showCreateNewQaBtn,
        aiTag,
        questionBotKey,
        lucaAskChatId,
        askId,
        refreshCommRecordsByEffect,
        collectFlag,
        isLucaAsk,
    } = qaConfig;
    const [visible, setVisibe] = useState(false);
    const [title, setTilte] = useState('');
    const [commRecordsIconShow, setCommRecordsIconShow] = useState(false);
    const timer = useRef<ReturnType<typeof setInterval>>();
    const abortController = useRef<any>();

    const controlCommRecordsIconShow = useCallback((status: boolean) => {
        setCommRecordsIconShow(status);
    }, []);

    const handleCloseQaRecordDrawer = useCallback(() => {
        setVisibe(false);
    }, []);

    const simulateStreamReply = (content = '') =>
        new Promise(resolve => {
            let index = -1;
            timer.current = setInterval(() => {
                setTilte(oldText => {
                    if (index >= content.length - 1) {
                        resolve(clearInterval(timer.current));
                        timer.current = undefined;
                        return oldText;
                    }
                    index++;
                    if (index === 0) return content[index];
                    return oldText + content[index];
                });
            }, 100);
        });

    const handleControlRightsModalVisible = () => {
        // 跳转
        navigateExample.linkTo('equity', { lucaId, projectId });
    };

    const updateTitle = async (content: string) => {
        const newContent = content.replace(/\n/g, ' ').slice(0, 150);
        await simulateStreamReply(newContent);
        updateQaConfig({ askChatTitle: content });
    };

    const refineProblem = async (params: { content: string; questionBotKey: string; lucaAskChatId: string }, isCreateQa = false) => {
        try {
            isCreateQa && updateTitle(params.content);
            abortController.current = addRequestController('fetch');
            const headers = await getChatSseHeaders(params.questionBotKey);
            const { result } = await get_summary(
                { lucaAskChatId: params.lucaAskChatId, content: params.content },
                { cancelToken: abortController.current?.token, headers }
            );
            if (!result) return false;
            updateQaConfig({ questionTag: 1 });
            !isCreateQa && updateTitle(params.content);
            return true;
        } catch (error) {
            return error;
        }
    };

    const handleOpenHistorySession = () => {
        if (!getQaStore()?.validateCanSend('ocrAndGptReplying')) return;
        setVisibe(true);
    };

    useEffect(() => {
        eventBusExample.subscribe('NavgationBar', async ({ type, params = {} }: INoticeData) => {
            const { content, isCreateQa, lucaAskChatId, excuteRef } = params;
            switch (type) {
                case 'refineProblem':
                case 'refineProblemFromOcr':
                    if (questionTag === 1 || !lucaAskChatId) return;
                    if (type === 'refineProblemFromOcr') {
                        updateQaConfig({ questionTag: 1 });
                        updateTitle(content);
                    } else {
                        const result = await refineProblem({ lucaAskChatId, questionBotKey: questionBotKey || '', content }, isCreateQa);
                        if (excuteRef) {
                            result === 'send_cancel' ? excuteRef.reject('send_cancel') : excuteRef.resolve(result);
                        }
                    }
                    break;
                default:
                    abortController.current?.abort?.('切换会话/关闭答疑组件取消请求');
                    timer.current && clearInterval(timer.current);
                    setVisibe(false);
                    setTilte('');
                    setCommRecordsIconShow(false);
                    break;
            }
        });
    }, [questionTag, questionBotKey]);

    useEffect(() => {
        setTilte(askChatTitle.replace(/\n/g, ' ').slice(0, 150));
    }, [askChatTitle]);

    const renderHeader = useMemo(
        () => (
            <NavigationHeaderWapper>
                {transferLaborState === 1 ? (
                    <div className="navigation-bar__teacher">
                        <div className="navigation-bar__avatar"></div>
                        <span>名师答疑</span>
                    </div>
                ) : (
                    <div className="navigation-bar__ai">
                        <div className="navigation-bar__ai-logo"></div>
                    </div>
                )}
                {aiTag === 1 && <div className="navigation-bar__recharge" onClick={handleControlRightsModalVisible} />}
                {showCreateNewQaBtn ? (
                    <NewQuestionBtn onClick={createNewQuestion} />
                ) : (
                    <CollectAction
                        className="navigation-bar__collect-container"
                        initState={collectFlag}
                        type={isLucaAsk === 1 ? 'luca_qa' : 'gbot_detail'}
                        id={commId}
                        needToastInQa
                        onChange={collectFlag => updateQaConfig({ collectFlag })}
                    />
                )}
            </NavigationHeaderWapper>
        ),
        [showCreateNewQaBtn, transferLaborState, lucaId, collectFlag, projectId, aiTag, commId]
    );

    const renderTopic = useMemo(() => {
        let typeStr = `${projectName}-${subjectName}`;
        if (twoLevelSubjectName) typeStr += `-${twoLevelSubjectName}`;
        return (
            <div className="navigation-bar__topic">
                {title ? (
                    <div className="navigation-bar__question-describe">
                        <div>{title}</div>
                    </div>
                ) : (
                    <div className="navigation-bar__question-type">{typeStr}</div>
                )}
                {commRecordsIconShow && (
                    <div className="navigation-bar__qa-records-btn" onClick={handleOpenHistorySession}>
                        全部
                    </div>
                )}
            </div>
        );
    }, [title, projectName, subjectName, twoLevelSubjectName, commRecordsIconShow]);

    return (
        <div className="navigation-bar">
            {renderHeader}
            {renderTopic}
            <CommRecordsDrawer
                visible={visible}
                lucaAskChatId={lucaAskChatId}
                askId={askId}
                askChatTitle={askChatTitle}
                refreshCommRecordsByEffect={refreshCommRecordsByEffect}
                onClose={handleCloseQaRecordDrawer}
                onControlCommRecordsIconShow={controlCommRecordsIconShow}
            />
        </div>
    );
};

export default memo(NavgationBar);
